import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { getCustomVariantDataAttributes } from '@unifyapps/carbon/no-code/utils/getCustomVariantDataAttributes';
import { getConditionalPropertyValue } from '@unifyapps/carbon/utils/conditionalValues';
import type { IconBlockStateType, IconComponentType } from '@unifyapps/defs/blocks/Icon/types';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { Box } from '@unifyapps/ui/components/Box';
import type { SyntheticEvent } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useBlockTooltip } from '../common/hooks/useBlockTooltip';
import { Icon as BaseIcon } from '../common/Icon';
import IconBadge from './components/IconBadge';
import { normalizeIconTooltip } from './utils/normalizeIconTooltip';

/** all known Icon Appearance keys — anything else is a custom variant dimension */
const KNOWN_APPEARANCE_KEYS = new Set([
  'variant',
  'size',
  'styles',
  'featured',
  'transparent',
  'visibility',
  'className',
  'customStyles',
]);

function Icon({
  // oxlint-disable-next-line no-unused-vars
  blockState,
  attributesAndEventListeners,
  events,
  computedBlockState,
  dndRef,
  block,
  component,
  registry,
}: BlockComponentProps<IconComponentType, IconBlockStateType, IconBlockStateType>) {
  const {
    content: { icon, addOns },
    appearance,
  } = computedBlockState;

  const { variant, size, styles, featured, transparent } = appearance;

  const badge = addOns && 'badge' in addOns ? addOns?.badge : {};

  const BlockRenderer = registry.getBlockRenderer();
  const { slots } = component;
  const tooltipSlotBlockId = slots?.tooltip?.blockId;

  // icons saved before the add-on tooltip rework use a flat { text, placement, width }
  // shape; normalize to the shared BlockTooltipAddOn so old and new data both render.
  const tooltipAddOn = useMemo(() => normalizeIconTooltip(addOns?.tooltip), [addOns?.tooltip]);

  const iconName = getConditionalPropertyValue({ value: icon });
  const IconComponent = getIconFromRegistry(iconName);

  const { emitEvent } = useBlockEvents(events, blockState.id);
  const onClick = useCallback(
    (e: SyntheticEvent) => {
      void emitEvent({
        eventType: EventTypeEnum.OnClick,
        domEvent: e,
      });
    },
    [emitEvent],
  );

  const hasClickEvent = events?.some((event) => event.eventType === 'onClick');

  // custom variant dimensions (e.g. iconPack → data-icon-pack="outline") applied as data attributes
  // on the outer wrapper so CSS selectors can target/override color, shape, etc.
  const customVariantDataAttrs = useMemo(
    () =>
      getCustomVariantDataAttributes(
        appearance as unknown as Record<string, unknown> | undefined,
        KNOWN_APPEARANCE_KEYS,
      ),
    [appearance],
  );

  const iconEl = (
    // icon generated do not have forward ref, so we need to wrap it in a Box for the
    // tooltip to have a dom element to attach its ref to
    <Box className="relative h-fit w-fit" ref={dndRef} {...attributesAndEventListeners}>
      <BaseIcon
        customCSS={block.additional?.customCSS}
        customStyles={block.component?.appearance?.customStyles}
        customVariantDataAttrs={customVariantDataAttrs}
        featured={featured}
        hasClickEvent={hasClickEvent}
        icon={iconName}
        onClick={onClick}
        size={size}
        styles={styles}
        transparent={transparent}
        variant={variant}
      />
      {badge ? (
        <IconBadge
          badge={badge}
          customVariantDataAttrs={customVariantDataAttrs}
          iconSize={size}
          variant={variant}
        />
      ) : null}
    </Box>
  );

  const { wrapWithTooltip, tooltipBuilderNode } = useBlockTooltip({
    blockId: blockState.id,
    tooltipAddOn,
    tooltipSlotBlockId,
    BlockRenderer,
  });

  if (!IconComponent) {
    return null;
  }

  return (
    <>
      {wrapWithTooltip(iconEl)}
      {tooltipBuilderNode}
    </>
  );
}

export default memo(Icon);
